/**
 * Kennzahlenkachel fuer das Dashboard.
 *
 * Eine Kachel darf eine Handlung tragen: Wer "Lizenzen an deaktivierten
 * Konten: 37" liest, will wissen, welche 37 – die Zahl allein ist eine
 * Sackgasse. Mit `to` wird die Kachel ein echter Verweis (Anker, damit
 * Tastatur, Mittelklick und "in neuem Tab oeffnen" funktionieren), mit
 * `onClick` eine Schaltflaeche fuer Filter auf derselben Seite.
 */
import { Body1, Card, Caption1, Title2, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { ChevronRightRegular } from '@fluentui/react-icons';
import type { KeyboardEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingVerticalL,
    minWidth: '160px',
    flex: '1 1 180px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground2,
  },
  value: { lineHeight: '1.2' },
  // Der Verweis traegt die Groessenangaben, damit die Kachel im Flex-Layout
  // genauso liegt wie eine Kachel ohne Handlung.
  link: {
    display: 'flex',
    minWidth: '160px',
    flex: '1 1 180px',
    textDecorationLine: 'none',
    color: 'inherit',
  },
  actionable: {
    cursor: 'pointer',
    width: '100%',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    ':active': { backgroundColor: tokens.colorNeutralBackground1Pressed },
  },
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalS },
  chevron: { color: tokens.colorNeutralForeground3, flexShrink: 0 },
  hint: { color: tokens.colorNeutralForeground3 },
  // Griffel erlaubt keine CSS-Kurzschreibweisen; daher Langform je Eigenschaft.
  accentDefault: {
    borderInlineStartWidth: '4px',
    borderInlineStartStyle: 'solid',
    borderInlineStartColor: tokens.colorBrandStroke1,
  },
  accentSuccess: {
    borderInlineStartWidth: '4px',
    borderInlineStartStyle: 'solid',
    borderInlineStartColor: tokens.colorPaletteGreenBorderActive,
  },
  accentWarning: {
    borderInlineStartWidth: '4px',
    borderInlineStartStyle: 'solid',
    borderInlineStartColor: tokens.colorPaletteDarkOrangeBorderActive,
  },
});

export interface KpiCardProps {
  label: string;
  value: string;
  hint?: string;
  icon?: ReactNode;
  accent?: 'default' | 'success' | 'warning';
  /** Ziel einer Detailansicht; macht die Kachel zu einem Verweis. */
  to?: string;
  /** Handlung auf derselben Seite (z. B. Filter setzen); macht die Kachel zu einer Schaltflaeche. */
  onClick?: () => void;
  /** Kurzer Text, der beim Zeigen erklaert, was der Klick bewirkt. */
  actionHint?: string;
}

export function KpiCard({ label, value, hint, icon, accent = 'default', to, onClick, actionHint }: KpiCardProps) {
  const styles = useStyles();
  const accentClass =
    accent === 'success' ? styles.accentSuccess : accent === 'warning' ? styles.accentWarning : styles.accentDefault;
  const actionable = to !== undefined || onClick !== undefined;

  const body = (
    <Card
      className={mergeClasses(styles.card, accentClass, actionable && styles.actionable)}
      title={actionHint}
      {...(onClick !== undefined
        ? {
            role: 'button',
            tabIndex: 0,
            onClick,
            // Fluent-Card ist ein div: Tastaturbedienung muss von Hand kommen.
            onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick();
              }
            },
          }
        : {})}
    >
      <div className={styles.headerRow}>
        <div className={styles.header}>
          {icon}
          <Body1>{label}</Body1>
        </div>
        {actionable ? <ChevronRightRegular className={styles.chevron} aria-hidden /> : null}
      </div>
      <Title2 className={styles.value}>{value}</Title2>
      {hint ? <Caption1 className={styles.hint}>{hint}</Caption1> : null}
    </Card>
  );

  if (to !== undefined) {
    return (
      <Link to={to} className={styles.link} aria-label={actionHint ? `${label}: ${actionHint}` : undefined}>
        {body}
      </Link>
    );
  }
  return body;
}
